import { useAppStore } from '@/stores/app';
import * as Sentry from '@sentry/vue';
import { Debugout } from 'debugout.js';
import type { App } from 'vue';
import type { Router } from 'vue-router';

const APP_VERSION = import.meta.env.npm_package_version;
const APP_ENV: string = import.meta.env.VITE_APP_ENV;
const SENTRY_DSN: string = import.meta.env.VITE_SENTRY_DSN;

const bugout = new Debugout({
  autoTrim: true,
  maxLines: 300,
  quoteStrings: false,
  realTimeLoggingOn: false,
  useLocalStorage: false,
  useTimestamps: true,
  maxDepth: 4,
});

// Export bugout for use in other files (e.g., useFetch.ts)
export { bugout };

// List of known expected API errors that shouldn't be reported
const EXPECTED_API_ERRORS = [
  'UNAUTHORIZED_ERROR',
  'INVALID_CREDENTIALS',
  'SESSION_EXPIRED',
  'VALIDATION_ERROR',
  'RECAPTCHA_FAILED',
];

// Check if error is from a known problematic source
const isThirdPartyError = (event: Sentry.ErrorEvent): boolean => {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
  return frames.some(
    (frame) =>
      frame.filename?.includes('gtag') ||
      frame.filename?.includes('analytics') ||
      frame.filename?.includes('clarity') ||
      frame.filename?.includes('yandex') ||
      frame.filename?.includes('hantana') ||
      frame.filename?.includes('recaptcha'),
  );
};

const sentry = {
  install(app: App, router: Router) {
    if (SENTRY_DSN && APP_ENV === 'production') {
      Sentry.init({
        environment: import.meta.env.MODE,
        app,
        dsn: SENTRY_DSN as string,
        release: APP_VERSION,

        // Integrations
        integrations: [
          Sentry.browserTracingIntegration({
            router,
            enableInp: true, // Track Interaction to Next Paint (Core Web Vital)
          }),
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
            // Mask sensitive inputs
            maskAllInputs: true,
            // Network capture for debugging
            networkDetailAllowUrls: [window.location.origin],
            networkCaptureBodies: true,
          }),
          Sentry.httpClientIntegration({
            failedRequestStatusCodes: [
              [400, 400],
              [402, 499],
              [500, 599],
            ],
            failedRequestTargets: [/api\//],
          }),
          Sentry.reportingObserverIntegration({
            types: ['crash', 'deprecation'],
          }),
          // Add breadcrumbs for better debugging context
          Sentry.breadcrumbsIntegration({
            console: true,
            dom: true,
            fetch: true,
            history: true,
            xhr: true,
          }),
        ],

        // Performance - use dynamic sampling for better efficiency
        tracesSampleRate: 0.1, // 10% of transactions for performance

        // Error sampling - keep at 100% for errors
        sampleRate: 1,

        // Session Replay - reduced for performance
        replaysSessionSampleRate: 0.05, // 5% of sessions
        replaysOnErrorSampleRate: 1.0, // 100% when error occurs

        // Comprehensive error filtering
        ignoreErrors: [
          // Browser/DOM errors (not actionable)
          'ResizeObserver loop',
          'ResizeObserver loop limit exceeded',
          'ResizeObserver loop completed with undelivered notifications',
          'Non-Error promise rejection captured',
          'Non-Error exception captured',

          // Network errors (usually connectivity issues)
          'Network request failed',
          'NetworkError',
          'Failed to fetch',
          'Load failed',
          'fetch failed',
          'AbortError',
          'TypeError: Failed to fetch',
          'TypeError: NetworkError',
          'TypeError: cancelled',

          // Chunk loading (handled by retry logic)
          /Loading chunk .* failed/,
          /Loading CSS chunk .* failed/,
          'ChunkLoadError',
          /Importing a module script failed/,

          // Browser-specific
          'Script error',
          'Script error.',
          /^Uncaught$/,

          // User actions
          'User denied',
          'User rejected',
          'Permission denied',

          // Third-party / Extensions
          /^chrome-extension:\/\//,
          /^moz-extension:\/\//,
          /^safari-extension:\/\//,
          'instantSearchSDKJSBridgeClear498',
          '__gCrWeb',
          'celogin',

          // Recaptcha errors
          /recaptcha/i,
          /reCAPTCHA/,

          // Common false positives
          'null is not an object',
          "undefined is not an object (evaluating 'a.L')",
          'Cannot read properties of null',
          "Cannot read property 'style' of null",
        ],

        denyUrls: [
          // Browser extensions
          /extensions\//i,
          /^chrome:\/\//i,
          /^chrome-extension:\/\//i,
          /^moz-extension:\/\//i,
          /^safari-extension:\/\//i,
          /^safari-web-extension:\/\//i,
          // Analytics & third-party scripts
          /google-analytics\.com/i,
          /googletagmanager\.com/i,
          /clarity\.ms/i,
          /mc\.yandex\.ru/i,
          /hantana/i,
          /gstatic\.com/i,
          /recaptcha/i,
        ],

        // Event processing with better filtering
        beforeSend(event) {
          // Filter out third-party errors
          if (isThirdPartyError(event)) {
            return null;
          }

          // Filter out expected errors (e.g., user just needs to login)
          const errorMessage = event.exception?.values?.[0]?.value || '';
          if (EXPECTED_API_ERRORS.some((err) => errorMessage.includes(err))) {
            return null;
          }

          // Add user context
          try {
            const appStore = useAppStore();
            if (appStore.profile?.userName) {
              event.user = {
                username: appStore.profile.userName,
              };
            }
          } catch {
            // Store may not be initialized yet
          }

          // Attach debug logs
          event.extra = event.extra ?? {};
          event.extra.debugLog = bugout.getLog();

          // Add browser/device context
          event.tags = event.tags ?? {};
          event.tags.locale = document.documentElement.lang || 'unknown';
          event.tags.online = navigator.onLine ? 'yes' : 'no';
          event.tags.visibility = document.visibilityState;

          return event;
        },

        // Filter breadcrumbs for cleaner data
        beforeBreadcrumb(breadcrumb) {
          // Don't log XHR/fetch to analytics services
          if (breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') {
            const url = breadcrumb.data?.url || '';
            if (
              url.includes('google-analytics') ||
              url.includes('clarity') ||
              url.includes('yandex') ||
              url.includes('hantana') ||
              url.includes('sentry')
            ) {
              return null;
            }
          }

          // Limit console breadcrumbs to errors and warnings
          if (breadcrumb.category === 'console') {
            if (breadcrumb.level !== 'error' && breadcrumb.level !== 'warning') {
              return null;
            }
          }

          return breadcrumb;
        },

        // Other options
        enabled: true,
        logErrors: true,
        attachStacktrace: true,
        autoSessionTracking: true,
        sendDefaultPii: false,
        normalizeDepth: 5, // Deeper object serialization for better debugging
        maxBreadcrumbs: 50, // Limit breadcrumbs for performance
      });

      // Log global errors to bugout (Sentry captures automatically)
      window.addEventListener('error', (e) => {
        bugout.log('Global error:', e.message, e.filename, e.lineno);
      });

      window.addEventListener('unhandledrejection', (e) => {
        bugout.log('Unhandled rejection:', e.reason);
      });

      // Vue error handler for uncaught errors in components
      app.config.errorHandler = (err, instance, info) => {
        bugout.log('Vue error:', err, info);
        Sentry.captureException(err, {
          extra: {
            vueInfo: info,
            componentName: instance?.$options?.name || 'Unknown',
          },
        });
      };

      // Vue warning handler (only log, don't send to Sentry)
      app.config.warnHandler = (msg, _instance, trace) => {
        bugout.log('Vue warning:', msg, trace);
      };
    }
  },
};

class HandledException extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'HandledException';
  }
}

// API errors that are expected and shouldn't be reported to Sentry
const IGNORED_API_ERRORS = [
  'UNAUTHORIZED_ERROR',
  'INVALID_CREDENTIALS',
  'SESSION_EXPIRED',
  'VALIDATION_ERROR',
  'RECAPTCHA_FAILED',
  'USER_NOT_FOUND',
  'INVALID_TOKEN',
  'TOKEN_EXPIRED',
  'RATE_LIMIT_EXCEEDED',
];

// Errors that should be reported as warnings instead of errors
const WARNING_LEVEL_ERRORS = [
  'USER_MAXIMUM_LIMIT_REACHED',
  'USER_IMPORT_LIMIT_EXCEEDED',
  'USER_INVALID_INFO',
  'CHANNEL_LIMIT_EXCEEDED',
  'FILE_TOO_LARGE',
  'INVALID_FILE_TYPE',
];

/**
 * Capture an exception to Sentry with additional context
 * @param error - The error to capture
 * @param context - Additional context to attach to the error
 */
export function captureSystemException(error: any, context?: Record<string, any>) {
  // Only send to Sentry in production
  if (APP_ENV !== 'production' || !SENTRY_DSN) {
    console.error('Exception:', error, context);
    return;
  }

  let exception = error;
  if (!(error instanceof Error)) {
    exception = new HandledException(
      error.title || error.name || error.detailedError || 'Unknown Error: Check additional data section',
    );
  }

  Sentry.captureException(exception, {
    extra: {
      originalError: error,
      ...context,
    },
  });
}

/**
 * Capture an API error to Sentry
 * @param url - The API endpoint URL
 * @param errorCode - The error code from the API
 * @param status - HTTP status code
 * @param errorDetails - Additional error details from the response
 */
export function captureApiError(
  url: string,
  errorCode: string | number,
  status?: number,
  errorDetails?: Record<string, any>,
) {
  if (APP_ENV !== 'production' || !SENTRY_DSN) {
    return;
  }

  const errorCodeStr = String(errorCode);

  // Skip ignored errors (expected user errors)
  if (IGNORED_API_ERRORS.some((err) => errorCodeStr.includes(err))) {
    return;
  }

  // Determine severity level
  const isWarning = WARNING_LEVEL_ERRORS.some((err) => errorCodeStr.includes(err));
  const isServerError = status && status >= 500;
  const level: Sentry.SeverityLevel = isServerError ? 'error' : isWarning ? 'warning' : 'error';

  // Clean URL for better grouping (remove query params and IDs)
  const cleanUrl = url
    .replace(/\/\d+/g, '/:id')
    .replace(/\?.*$/, '')
    .replace(/\/[a-f0-9-]{36}/gi, '/:uuid');

  Sentry.captureMessage(`API Error: ${errorCodeStr} - ${cleanUrl}`, {
    level,
    fingerprint: ['api-error', cleanUrl, errorCodeStr], // Better error grouping
    tags: {
      type: 'api_error',
      errorCode: errorCodeStr,
      httpStatus: status?.toString() || 'unknown',
      endpoint: cleanUrl,
      isServerError: isServerError ? 'yes' : 'no',
    },
    extra: {
      url,
      errorCode,
      status,
      errorDetails,
      debugLog: bugout.getLog(),
    },
  });
}

/**
 * Capture a custom event/metric to Sentry
 * @param name - Event name
 * @param data - Event data
 * @param level - Severity level
 */
export function captureEvent(name: string, data?: Record<string, any>, level: Sentry.SeverityLevel = 'info') {
  if (APP_ENV !== 'production' || !SENTRY_DSN) {
    return;
  }

  Sentry.captureMessage(name, {
    level,
    tags: { type: 'custom_event' },
    extra: data,
  });
}

/**
 * Add breadcrumb for better debugging context
 * @param message - Breadcrumb message
 * @param category - Category (e.g., 'user', 'navigation', 'api')
 * @param data - Additional data
 */
export function addBreadcrumb(message: string, category = 'custom', data?: Record<string, any>) {
  if (APP_ENV !== 'production' || !SENTRY_DSN) {
    return;
  }

  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
}

/**
 * Set user context for Sentry (call after login)
 * @param user - User information or null to clear
 */
export function setSentryUser(user: { id?: string | number; username?: string; email?: string } | null) {
  if (APP_ENV !== 'production' || !SENTRY_DSN) {
    return;
  }

  if (user) {
    Sentry.setUser({
      id: user.id?.toString(),
      username: user.username,
      email: user.email,
    });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Clear user context from Sentry (call on logout)
 */
export function clearSentryUser() {
  setSentryUser(null);
}

export default sentry;
